/**
 * Employee Referrals — 3 surfaces in one file:
 *  - "Refer a friend" composer (any employee)
 *  - "My referrals" list (caller's submitted referrals + live candidate stage)
 *  - "Inbox" (HR/recruiter view of all incoming referrals)
 */
import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { UserPlus, Briefcase, Gift, Trophy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const HR_ROLES = ["super_admin", "company_admin", "country_head", "region_head", "hr_manager"];
const RECRUIT_ROLES = [...HR_ROLES, "branch_manager", "recruiter"];

const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

const STAGE_COLOR = {
  applied: "bg-zinc-100 text-zinc-700",
  screening: "bg-blue-100 text-blue-700",
  shortlisted: "bg-violet-100 text-violet-700",
  interview: "bg-amber-100 text-amber-700",
  offer_pending: "bg-orange-100 text-orange-700",
  offer_sent: "bg-orange-200 text-orange-800",
  offer_accepted: "bg-emerald-100 text-emerald-700",
  hired: "bg-emerald-600 text-white",
  rejected: "bg-rose-100 text-rose-700",
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };

export default function Referrals() {
  const { user } = useAuth();
  const isRecruiter = RECRUIT_ROLES.includes(user?.role);
  const [tab, setTab] = useState("mine");
  const [openPositions, setOpenPositions] = useState([]);
  const [mine, setMine] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(null);  // null | requisition object

  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        api.get("/recruitment/referrals/open-positions"),
        api.get("/recruitment/referrals/mine"),
      ];
      if (isRecruiter) calls.push(api.get("/recruitment/referrals"));
      const results = await Promise.all(calls);
      setOpenPositions(results[0].data);
      setMine(results[1].data);
      if (isRecruiter) setInbox(results[2].data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (loading) return <AppShell><div className="p-8 text-zinc-500">Loading…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="referrals-page">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <UserPlus size={22}/> Employee Referrals
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Know someone great? Refer them here. You earn a bonus when they&apos;re hired.
          </p>
        </header>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="mine" data-testid="ref-tab-mine">Open positions ({openPositions.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="ref-tab-history">My referrals ({mine.length})</TabsTrigger>
            {isRecruiter && <TabsTrigger value="inbox" data-testid="ref-tab-inbox">Inbox ({inbox.length})</TabsTrigger>}
          </TabsList>

          <TabsContent value="mine" className="mt-4">
            {openPositions.length === 0 ? (
              <Card className="p-8 text-sm text-zinc-500 text-center">No open positions right now.</Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {openPositions.map(r => (
                  <Card key={r.id} className="p-4" data-testid={`open-req-${r.id}`}>
                    <div className="flex items-start gap-2">
                      <Briefcase size={18} className="text-violet-700 mt-0.5"/>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium leading-tight">{r.title}</div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {r.department || "—"}{r.location ? ` · ${r.location}` : ""}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-1">{r.code}</div>
                      </div>
                    </div>
                    {(r.skills || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {r.skills.slice(0, 5).map(s => (
                          <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                        ))}
                      </div>
                    )}
                    <Button size="sm" className="w-full mt-3" onClick={() => setComposing(r)} data-testid={`refer-btn-${r.id}`}>
                      <UserPlus size={14} className="mr-1"/> Refer someone
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <ReferralTable rows={mine} emptyText="You haven't referred anyone yet — earn a bonus!"/>
          </TabsContent>

          {isRecruiter && (
            <TabsContent value="inbox" className="mt-4">
              <Card className="p-3 mb-3 bg-amber-50 border-amber-200 text-xs text-amber-900">
                <Gift size={14} className="inline mr-1"/> Hire any of these and the system will auto-trigger a referral bonus payout to the referrer.
              </Card>
              <ReferralTable rows={inbox} showReferrer emptyText="No referrals yet."/>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {composing && (
        <ReferComposer
          requisition={composing}
          onClose={() => setComposing(null)}
          onSubmitted={() => { setComposing(null); setTab("history"); load(); }}
        />
      )}
    </AppShell>
  );
}

function ReferralTable({ rows, showReferrer, emptyText }) {
  if (rows.length === 0) {
    return <Card className="p-8 text-sm text-zinc-500 text-center">{emptyText}</Card>;
  }
  return (
    <Card className="p-0 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50">
          <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500 border-b">
            <th className="py-2.5 px-4 font-medium">Candidate</th>
            <th className="py-2.5 px-2 font-medium">Role</th>
            {showReferrer && <th className="py-2.5 px-2 font-medium">Referred by</th>}
            <th className="py-2.5 px-2 font-medium">Submitted</th>
            <th className="py-2.5 px-2 font-medium">Current stage</th>
            <th className="py-2.5 px-2 font-medium">Bonus</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const stageColor = STAGE_COLOR[r.candidate_stage] || STAGE_COLOR.applied;
            return (
              <tr key={r.id} className="border-b last:border-0" data-testid={`ref-row-${r.id}`}>
                <td className="py-3 px-4">
                  <div className="font-medium">{r.candidate_name}</div>
                  <div className="text-xs text-zinc-500">{r.candidate_email}</div>
                </td>
                <td className="py-3 px-2">{r.requisition_title}</td>
                {showReferrer && <td className="py-3 px-2 text-xs">{r.referrer_name}</td>}
                <td className="py-3 px-2 text-xs text-zinc-500">{fmtDate(r.created_at)}</td>
                <td className="py-3 px-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${stageColor}`}>
                    {r.candidate_stage}
                  </span>
                </td>
                <td className="py-3 px-2">
                  {r.candidate_stage === "hired" ? (
                    <Badge className="bg-emerald-600 text-white text-[10px]">
                      <Trophy size={10} className="inline mr-0.5"/> Eligible
                    </Badge>
                  ) : r.candidate_stage === "rejected" ? (
                    <span className="text-[11px] text-zinc-400">—</span>
                  ) : (
                    <span className="text-[11px] text-zinc-500">Pending hire</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function ReferComposer({ requisition, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    candidate_name: "",
    candidate_email: "",
    candidate_phone: "",
    candidate_current_company: "",
    candidate_current_title: "",
    candidate_experience_years: "",
    candidate_location: "",
    relationship: "Former colleague",
    why_recommend: "",
  });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.candidate_name.trim() || !form.candidate_email.trim()) {
      toast.error("Name and email are required"); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("requisition_id", requisition.id);
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v != null) fd.append(k, v);
      });
      if (resume) fd.append("resume", resume);
      await api.post("/recruitment/referrals", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Referral submitted — HR will get back to you soon!");
      onSubmitted();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="refer-composer">
        <DialogHeader>
          <DialogTitle>Refer someone for {requisition.title}</DialogTitle>
          <DialogDescription>
            {requisition.department || "—"}{requisition.location ? ` · ${requisition.location}` : ""}
            {" · "}You earn a bonus when they&apos;re hired.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Their name *</Label><Input value={form.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} data-testid="ref-c-name"/></div>
            <div><Label className="text-xs">Their email *</Label><Input type="email" value={form.candidate_email} onChange={(e) => set("candidate_email", e.target.value)} data-testid="ref-c-email"/></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.candidate_phone} onChange={(e) => set("candidate_phone", e.target.value)}/></div>
            <div><Label className="text-xs">Location</Label><Input value={form.candidate_location} onChange={(e) => set("candidate_location", e.target.value)}/></div>
            <div><Label className="text-xs">Current company</Label><Input value={form.candidate_current_company} onChange={(e) => set("candidate_current_company", e.target.value)}/></div>
            <div><Label className="text-xs">Current title</Label><Input value={form.candidate_current_title} onChange={(e) => set("candidate_current_title", e.target.value)}/></div>
            <div><Label className="text-xs">Years of experience</Label><Input type="number" step="0.5" value={form.candidate_experience_years} onChange={(e) => set("candidate_experience_years", e.target.value)}/></div>
            <div>
              <Label className="text-xs">How do you know them?</Label>
              <select value={form.relationship} onChange={(e) => set("relationship", e.target.value)} className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded text-sm bg-white">
                <option>Former colleague</option>
                <option>Current colleague</option>
                <option>College batchmate</option>
                <option>Friend</option>
                <option>Family</option>
                <option>Acquaintance</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Why do you recommend them?</Label>
            <Textarea rows={3} value={form.why_recommend} onChange={(e) => set("why_recommend", e.target.value)} placeholder="A few lines about their strengths, work style, or relevant projects." data-testid="ref-c-why"/>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Resume (optional, PDF)</Label>
            <Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResume(e.target.files?.[0] || null)} data-testid="ref-c-resume"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="ref-c-submit">
            {submitting ? "Submitting…" : "Submit referral"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
